/**
 * The guides of the two report screens — «My reports» and «Report triage» — in the five parts every
 * screen guide has, and their guided tours (ADR 9044; ADR 9071).
 *
 * The report screens are shell routes, not a chapter's, so Documentation — which reads the chapters'
 * manifests — does not list them. Their guide is therefore read where the screen is: «Read the
 * guide» opens it in a drawer beside the page, and «Guided tour» walks the page, as on every chapter
 * screen. The tour only explains: it presses nothing (ADR 9044).
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GUIDE_PARTS, type ScreenGuide } from '../../../features/guides';
import { PAGE_CONTENT, PageHeader, Tour } from '../../../planning';
import { Drawer } from '../../../primitives';
import { GuideControls } from '../GuideControls';

import { FEEDBACK_PATH, FEEDBACK_TRIAGE_PATH } from './constants';

export const REPORT_SCREEN_GUIDES: Readonly<Record<string, ScreenGuide>> = {
  [FEEDBACK_PATH]: {
    purposeKey: 'feedback.guide.myReports.purpose',
    readingKey: 'feedback.guide.myReports.reading',
    actionsKey: 'feedback.guide.myReports.actions',
    statesKey: 'feedback.guide.myReports.states',
    sourceKey: 'feedback.guide.myReports.source',
    tour: [
      { anchor: 'feedback-my-reports-table', titleKey: 'feedback.tour.myReports.table.title', bodyKey: 'feedback.tour.myReports.table.body' },
      { anchor: 'feedback-my-report-open', titleKey: 'feedback.tour.myReports.open.title', bodyKey: 'feedback.tour.myReports.open.body' },
    ],
  },
  [FEEDBACK_TRIAGE_PATH]: {
    purposeKey: 'feedback.guide.triage.purpose',
    readingKey: 'feedback.guide.triage.reading',
    actionsKey: 'feedback.guide.triage.actions',
    statesKey: 'feedback.guide.triage.states',
    sourceKey: 'feedback.guide.triage.source',
    tour: [
      { anchor: 'feedback-triage-filters', titleKey: 'feedback.tour.triage.filters.title', bodyKey: 'feedback.tour.triage.filters.body' },
      { anchor: 'feedback-triage-table', titleKey: 'feedback.tour.triage.table.title', bodyKey: 'feedback.tour.triage.table.body' },
      { anchor: 'feedback-triage-open', titleKey: 'feedback.tour.triage.open.title', bodyKey: 'feedback.tour.triage.open.body' },
    ],
  },
};

const PART_HEADING: Readonly<Record<(typeof GUIDE_PARTS)[number], string>> = {
  purposeKey: 'feedback.guide.part.purpose',
  readingKey: 'feedback.guide.part.reading',
  actionsKey: 'feedback.guide.part.actions',
  statesKey: 'feedback.guide.part.states',
  sourceKey: 'feedback.guide.part.source',
};

/** A part's text: paragraphs on blank lines, a run of «- » lines as a list. Text only. */
function GuidePartText({ text }: { readonly text: string }): React.JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      {text.split(/\n\s*\n/).map((paragraph, index) => {
        const lines = paragraph.split('\n');
        return lines.every((line) => line.startsWith('- '))
          ? <ul key={index} className="text-body text-ink-primary list-disc ps-4">{lines.map((line, at) => <li key={at}>{line.slice(2)}</li>)}</ul>
          : <p key={index} className="text-body text-ink-primary">{paragraph}</p>;
      })}
    </div>
  );
}

/**
 * A report screen's frame: its section, its page header — with «Guided tour» and «Read the guide» —
 * the page's content, and what the two buttons open. The router draws the two report screens in it.
 */
export function ReportScreenFrame({ path, kickerKey, titleKey, children }: {
  readonly path: string;
  readonly kickerKey: string;
  readonly titleKey: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  const guide = REPORT_SCREEN_GUIDES[path];
  const [touring, setTouring] = useState(false);
  const [reading, setReading] = useState(false);
  const endTour = useCallback(() => setTouring(false), []);
  return (
    <section aria-label={t(titleKey)} className="flex min-w-0 flex-col">
      <PageHeader
        kickerKey={kickerKey}
        titleKey={titleKey}
        {...(guide === undefined ? {} : { guide: <GuideControls onTour={() => setTouring(true)} onRead={() => setReading(true)} /> })}
      />
      <div data-layout-content="" className={PAGE_CONTENT}>
        {children}
      </div>
      {guide !== undefined && touring && <Tour steps={guide.tour} onClose={endTour} />}
      {guide !== undefined && (
        <Drawer open={reading} onOpenChange={setReading} titleKey="feedback.guide.title">
          <div className="flex flex-col gap-4" data-report-guide="">
            {GUIDE_PARTS.map((part) => (
              <section key={part} className="flex flex-col gap-1">
                <h3 className="text-label text-ink-primary font-semibold">{t(PART_HEADING[part])}</h3>
                <GuidePartText text={t(guide[part])} />
              </section>
            ))}
          </div>
        </Drawer>
      )}
    </section>
  );
}
